<script lang="ts">
  /*
   * The Website put a 16px PNG beside each of these — ok.png, error.png, info.png. Same three meanings,
   * as tonal containers with inline icons: they scale, they take their colour from the theme, and they
   * cost no network request on a page that is already waiting for two APIs.
   *
   * LIVE REGIONS ARE OPT-IN, and that distinction is load-bearing rather than pedantry. Most notices
   * here are ordinary page content that happens to be tinted — the standing warning on the deletion
   * page, the renewal state on Billing. Marking those `role="status"` announces furniture on every
   * render, and it makes "the outcome of what I just did" unfindable among several equal siblings.
   *
   * So: `live` for a notice REPORTING AN OUTCOME (a save, a request that went through), and error always,
   * because an error is worth announcing whenever it appears.
   */
  import type { Snippet } from 'svelte'

  let {
    tone = 'info',
    live = false,
    children,
  }: { tone?: 'ok' | 'info' | 'warn' | 'error'; live?: boolean; children: Snippet } = $props()

  // `$derived`, not a plain const: a const would capture the props' initial values, so a notice whose
  // tone changes in place — an error replacing a saved-confirmation on the same page — would keep the
  // wrong role and stop being announced.
  const role = $derived(tone === 'error' ? 'alert' : live ? 'status' : undefined)

  const paths: Record<string, string> = {
    ok: 'M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z',
    info: 'M11 7h2v2h-2zm0 4h2v6h-2zm1-9a10 10 0 1 0 0 20 10 10 0 0 0 0-20z',
    warn: 'M1 21h22L12 2zm12-3h-2v-2h2zm0-4h-2v-4h2z',
    error: 'M11 15h2v2h-2zm0-8h2v6h-2zm1-5a10 10 0 1 0 0 20 10 10 0 0 0 0-20z',
  }
</script>

<div class="notice notice-{tone}" {role}>
  <svg class="notice-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d={paths[tone]} />
  </svg>
  <div>{@render children()}</div>
</div>
